<template>
  <div>
    <div class="video" v-if="disabled">
      <span class="play"></span>
      <img :src="cover" alt="" />
    </div>
    <div
      :id="id"
      v-else
      aliPlayer
      :class="{
        'aliPlayer-display-h5': display === 'h5',
        'aliPlayer-display-pc': display === 'pc'
      }"
    >
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: 'aliPlayer',
  props: {
    // 视频是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 适配H5/PC端样式 入参：h5/pc
    display: {
      type: String,
      default: 'pc'
    },
    // 封面
    cover: {
      type: String,
      default: ''
    },
    // 是否自动播放
    autoplay: {
      type: Boolean,
      default: true
    },
    // 播放器高度
    height: {
      type: String,
      default: '500px'
    },
    // 播放器宽度
    width: {
      type: String,
      default: '100%'
    },
    // 是否是直播
    isLive: {
      type: Boolean,
      default: false
    },
    // URL播放方式下，source属性设置中可通过JSON结构的键值对（Key-Value Pair）包括：
    //  "OD": "<原画URL>"
    //  "FD": "<流畅URL>"
    //  "LD"："<标清URL>"
    //  "SD": "<高清URL>"
    //  "HD": "<超清URL>"
    //  "2K": "<2K URL>"
    //  "4K": "<4K URL>"
    source: {
      type: Object,
      default: function () {
        return null
      }
    },
    // DRM加密播放相关参数
    drmOptions: {
      type: Object,
      default: function () {
        return {
          vid: '',
          accessKeyId: '',
          accessKeySecret: '',
          securityToken: '',
          region: 'cn-shanghai'
        }
      }
    },
    videoIndex: {
      type: Number,
      default: 0
    },
    currentIndex: {
      type: Number,
      default: 0
    },
    beforeCreated: {
      type: Function,
      default: null
    },
    params: {
      type: Object,
      default: function () {
        return null
      }
    }
  },
  data() {
    return {
      id: '',

      SOURCE_PARAMS: null,

      DRM_PARAMS: {
        isDrm: false,
        vid: '',
        accessKeyId: '',
        accessKeySecret: '',
        securityToken: '',
        region: 'cn-shanghai'
      },

      // 播放器实例
      aliPlayer: null,

      // 监听播放器dom
      observer: null,

      // 播放器组件
      components: []
    }
  },
  watch: {
    source: function (val) {
      this.SOURCE_PARAMS = val
      this.create()
    },
    drmOptions: function (val) {
      this.DRM_PARAMS = val
      this.create()
    },
    currentIndex: function (n) {
      if (n != this.videoIndex) {
        this.aliPlayer?.pause()
      }
    }
  },
  async created() {
    // 在实例化之前请求播放源
    if (this.beforeCreated && !this.disabled) {
      if (this.DRM_PARAMS.isDrm) {
        this.DRM_PARAMS = await this.beforeCreated(this.params)
      } else {
        this.SOURCE_PARAMS = await this.beforeCreated(this.params)
      }

      this.create()
    }
  },
  methods: {
    create() {
      // 视频禁用
      if (this.disabled) {
        return
      }
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
      if (this.aliPlayer) {
        this.aliPlayer.dispose()
        this.aliPlayer = null
      }
      this.id = `aliPlayer_${new Date().getTime()}`

      this.$nextTick(() => {
        this.aliPlayer = new Aliplayer({
          useH5Prism: true,
          playsinline: true,
          id: this.id,
          width: this.width,
          height: this.height,
          source: this.SOURCE_PARAMS
            ? JSON.stringify(this.SOURCE_PARAMS)
            : null,
          autoplay: this.autoplay,
          isLive: this.isLive,
          cover: this.cover,
          definition: 'HD,SD,LD',
          defaultDefinition: 'SD',
          keyShortCuts: true,
          skinLayout: [
            { name: 'bigPlayButton', align: 'cc' },
            { name: 'H5Loading', align: 'cc' },
            { name: 'errorDisplay', align: 'tlabs', x: 0, y: 0 },
            { name: 'infoDisplay' },
            { name: 'tooltip', align: 'blabs', x: 0, y: 56 },
            { name: 'thumbnail' },
            {
              name: 'controlBar',
              align: 'blabs',
              x: 0,
              y: 0,
              children: [
                { name: 'progress', align: 'blabs', x: 0, y: 44 },
                { name: 'playButton', align: 'tl', x: 15, y: 12 },
                { name: 'timeDisplay', align: 'tl', x: 10, y: 7 },
                { name: 'fullScreenButton', align: 'tr', x: 10, y: 12 },
                { name: 'subtitle', align: 'tr', x: 15, y: 12 },
                { name: 'setting', align: 'tr', x: 15, y: 12 },
                { name: 'volume', align: 'tr', x: 5, y: 10 }
              ]
            }
          ],
          // 播放器组件
          components: this.components,
          // DRM加密
          isDrm: this.DRM_PARAMS.isDrm,
          vid: this.DRM_PARAMS.vid,
          accessKeyId: this.DRM_PARAMS.accessKeyId,
          accessKeySecret: this.DRM_PARAMS.accessKeySecret,
          securityToken: this.DRM_PARAMS.securityToken,
          region: this.DRM_PARAMS.region
        })

        this.aliPlayer.on('ready', e => {
          this.$emit('ready', e, this.aliPlayer, `#${this.id}`)

          // 监听localStorage变化
          window.addEventListener('setItemEvent', e => {
            // 与当前视频类型不一致的视频全部暂停
            if (
              e.key === '_VIDEOCTRL_' &&
              e.newValue !== this.aliPlayer.getCurrentTime()
            ) {
              this.aliPlayer.pause()
            }
          })

          // dom 监听
          this.observer = new MutationObserver(mutationList => {
            for (var mutation of mutationList) {
              if (
                mutation.target.getAttribute('id') ||
                mutation.target.getAttribute('name') ||
                mutation.target.getAttribute('class')
              ) {
                this.observer.disconnect()
                this.aliPlayer.dispose()
                break
              }
            }
          })
          this.observer.observe(e.target.querySelector('video'), {
            attributes: true,
            childList: true,
            subtree: true
          })
        })

        // 监听播放
        // 记录当前播放的视频标识
        this.aliPlayer.on('play', e => {
          this.$emit('play', e, this.aliPlayer, `#${this.id}`)
          localStorage.setItem('_VIDEOCTRL_', this.aliPlayer.getCurrentTime())
        })

        // 文档说明
        // https://help.aliyun.com/document_detail/125572.htm?spm=a2c4g.11186623.0.0.10a64addrguwjS#section-d6l-d84-gwa
        const Events = [
          'pause',
          'canplay',
          'playing',
          'ended',
          'liveStreamStop',
          'onM3u8Retry',
          'hideBar',
          'showBar',
          'waiting',
          'timeupdate',
          'snapshoted',
          'requestFullScreen',
          'cancelFullScreen',
          'error',
          'startSeek',
          'completeSeek',
          'resolutionChange',
          'seiFrame',
          'rtsFallback'
        ]

        // 注册播放事件
        for (let i = 0; i < Events.length; i++) {
          this.aliPlayer.on(Events[i], e => {
            this.$emit(Events[i], e, this.aliPlayer, `#${this.id}`)
          })
        }
      })
    }
  }
}
</script>
<style lang="scss">
:root {
  --aliPlayer-color: #ff6600;
}
div[aliPlayer].prism-player {
  position: relative;
  z-index: 1;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  // 跑马灯层级
  .bullet-screen {
    z-index: 3;
  }
  // 进度条拖拽点位
  .prism-progress-played,
  // 音量大小
  .volume-value {
    background: var(--aliPlayer-color);
  }
  // 加载
  .prism-loading .circle {
    border-color: rgba(255, 102, 0, 0.2) rgba(255, 102, 0, 0.5)
      rgba(255, 102, 0, 0.7) rgba(255, 102, 0, 0.1);
  }
  // 设置菜单
  .prism-setting-selector ul li.current {
    color: var(--aliPlayer-color);
  }
  .prism-setting-selector ul li.current:before {
    border-left-color: var(--aliPlayer-color);
  }
}

// 适配PC端
div[aliPlayer].aliPlayer-display-pc {
  // 进度条拖拽点位
  .prism-progress-cursor {
    top: 0px;
    width: 5px;
    height: 5px;
    border-radius: 100%;
    background: var(--aliPlayer-color);
    box-shadow: 0 0 0 4px #fff, 0 0 0 7px rgba(255, 102, 0, 0.5);
    img {
      visibility: hidden;
    }
    &.cursor-hover {
      top: 0px !important;
    }
  }
  .prism-big-play-btn {
    width: 80px;
    height: 80px;
    .outter {
      width: 100%;
      height: 100%;
      box-shadow: 0 0 50px rgba(0, 0, 0, 0.3);
    }
  }
}

// 适配H5
div[aliPlayer].aliPlayer-display-h5 {
  // 进度条拖拽点位
  .prism-progress-cursor {
    background: var(--aliPlayer-color) !important;
    width: 18px !important;
    height: 18px !important;
    border-radius: 18px !important;
    top: -2px !important;
    box-shadow: 0 0 0 3px #fff, 0 0 0 10px rgba(255, 102, 0, 0.5);
    img {
      visibility: hidden;
    }
    // &.cursor-hover{
    //   top:0px !important;
    // }
  }
  .prism-volume,
  .prism-setting-btn,
  .prism-cc-btn,
  .preview-component-tip {
    display: none !important;
  }
  .prism-big-play-btn {
    width: 1.5rem;
    height: 1.5rem;
    .outter {
      width: 100%;
      height: 100%;
      box-shadow: 0 0 50px rgba(0, 0, 0, 0.3);
    }
  }
  .prism-time-display {
    margin-top: 22px !important;
  }
  .prism-play-btn,
  .prism-fullscreen-btn {
    width: 0.5rem;
    height: 0.5rem;
  }
  .prism-time-display {
    font-size: 0.25rem;
    height: 0.6rem;
    line-height: 0.6rem;
  }
  .prism-loading {
    width: 155px;
    height: 155px;
    z-index: 10;
  }
  .prism-loading .circle {
    border-radius: 100px;
    width: 145px;
    height: 145px;
  }
}
</style>
